/**
 * MultiYearTrendCard — WS-VIEW-2.
 *
 * The Normalization workspace shows one fiscal year at a time, so the analyst
 * could normalise all three years and still never see the trend between them
 * until the report was generated. This card puts the rollup in the workspace:
 * Revenue / Gross Profit / EBITDA / Adj. EBITDA year by year, plus the TTM and
 * multi-year-average summary.
 *
 * SCOPE — multi-YEAR: one entity, several periods. Combining separate legal
 * entities is multi-ENTITY consolidation, a different feature with its own
 * eliminations and ownership maths (CONSOL-FIX-1). Nothing here consolidates.
 *
 * NO NEW MATH. Every figure comes from `buildMultiYear` → `fm.derivePeriod`,
 * the same recompute-on-read the report engine uses, so this card can never
 * disagree with the Summary panel beside it. Formatting is passed in from the
 * page (`fmtCurrency` / `fmtAxis`) so the deal's reporting currency and the
 * axis abbreviation stay defined in exactly one place.
 */
import React, { useMemo } from 'react'
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts'
import { TrendingUpIcon } from 'lucide-react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { axisWidthFor, niceTicks } from '@/lib/chartScale'
import { buildMultiYear, yoyGrowth, type YearAdjustment, type YearRaw, type YearRollup } from '@/lib/multiYear'
import type { FlexibleLineItem } from '@/lib/flexibleLineItems'
import type { Cell } from '@/lib/financialMath'

// Brand ramp: navy → slate → gold. EBITDA and Adj. EBITDA share the gold hue at
// two opacities, matching the P&L Bridge convention (the headline figure is the
// solid one) so the two charts read as one family.
const SERIES = [
  { key: 'revenue', label: 'Revenue', fill: 'var(--color-primary)', opacity: 1 },
  { key: 'gross_profit', label: 'Gross Profit', fill: '#64748b', opacity: 1 },
  { key: 'ebitda', label: 'EBITDA', fill: 'var(--color-accent)', opacity: 0.5 },
  { key: 'adjusted_ebitda', label: 'Adj. EBITDA', fill: 'var(--color-accent)', opacity: 1 },
] as const

type MetricKey = (typeof SERIES)[number]['key']

export interface MultiYearTrendCardProps {
  /** Oldest-first, as the workspace holds them. TTM is the latest populated one. */
  years: readonly number[]
  pl: Record<number, YearRaw> | null | undefined
  adjustments: YearAdjustment[] | null | undefined
  /**
   * FIELDS-2: the deal's custom line items, unfiltered. Passed through to
   * `buildMultiYear` so a treatment applied on the Summary panel moves this
   * trend by the same amount — omitting them here is how the two panels start
   * quoting different Adj. EBITDA for the same fiscal year.
   */
  flexibleItems?: FlexibleLineItem[] | null
  fmtCurrency: (val: number | null | undefined) => string
  fmtAxis: (val: number) => string
  /** Highlighted in the table so the analyst can see which year they are editing. */
  activeYear?: number
  /** When supplied, a year column/bar is clickable and switches the P&L Input tab. */
  onSelectYear?: (year: number) => void
}

const fmtPct = (frac: number | null): string =>
  frac == null ? '—' : `${frac >= 0 ? '+' : ''}${(frac * 100).toFixed(1)}%`

const cellText = (v: Cell, fmt: (n: number | null | undefined) => string): string =>
  typeof v === 'number' ? fmt(v) : '—'

export const MultiYearTrendCard: React.FC<MultiYearTrendCardProps> = ({
  years, pl, adjustments, flexibleItems, fmtCurrency, fmtAxis, activeYear, onSelectYear,
}) => {
  const { populatedRows, ttm, average } = useMemo(
    () => buildMultiYear(years, pl, adjustments, flexibleItems),
    [years, pl, adjustments, flexibleItems],
  )

  // Only populated years are plotted. A year with nothing entered is *absent*,
  // not zero — a 0 bar would draw a revenue collapse that never happened.
  const chartData = useMemo(
    () => populatedRows.map(r => ({
      year: r.year,
      label: `FY${r.year}`,
      revenue: typeof r.revenue === 'number' ? r.revenue : 0,
      gross_profit: typeof r.gross_profit === 'number' ? r.gross_profit : 0,
      ebitda: typeof r.ebitda === 'number' ? r.ebitda : 0,
      adjusted_ebitda: typeof r.adjusted_ebitda === 'number' ? r.adjusted_ebitda : 0,
    })),
    [populatedRows],
  )

  // WS-VIEW-3: round tick steps, and a gutter measured from the widest tick
  // rather than a hardcoded 64px that a billion-scale deal would clip.
  const scale = useMemo(
    () => niceTicks(chartData.flatMap(d => SERIES.map(s => d[s.key]))),
    [chartData],
  )
  const axisWidth = useMemo(
    () => (scale ? axisWidthFor(scale.ticks, fmtAxis) : 64),
    [scale, fmtAxis],
  )

  // Growth against the previous *populated* year, so a gap in the middle of the
  // window does not silently become a comparison against zero.
  const growth = useMemo(() => {
    const out: Record<number, Partial<Record<MetricKey, number | null>>> = {}
    populatedRows.forEach((row, i) => {
      const prior = i > 0 ? populatedRows[i - 1] : null
      out[row.year] = prior
        ? Object.fromEntries(SERIES.map(s => [s.key, yoyGrowth(row[s.key], prior[s.key])]))
        : {}
    })
    return out
  }, [populatedRows])

  const emptyYears = years.filter(y => !populatedRows.some(r => r.year === y))

  return (
    <Card className="mt-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-brand-text flex items-center gap-2">
          <TrendingUpIcon className="h-4 w-4 text-brand-primary" />
          Multi-Year Trend
        </CardTitle>
        <CardDescription className="text-xs">
          {populatedRows.length > 0
            ? `Same entity, ${populatedRows.length} ${populatedRows.length === 1 ? 'period' : 'periods'} — every figure is the same derivation the report uses.`
            : 'Enter figures on any FY tab above and the trend appears here.'}
        </CardDescription>
      </CardHeader>

      <CardContent className="pt-0">
        {populatedRows.length === 0 ? (
          <p className="text-xs text-muted-foreground py-6 text-center">
            No fiscal year has figures yet — nothing to trend.
          </p>
        ) : (
          <>
            {populatedRows.length === 1 && (
              <p className="text-xs text-muted-foreground mb-2">
                Only FY{populatedRows[0].year} is populated — populate a second year to see a trend.
              </p>
            )}

            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis
                  tickFormatter={fmtAxis}
                  tick={{ fontSize: 10 }}
                  tickLine={false}
                  axisLine={false}
                  width={axisWidth}
                  {...(scale ? { domain: scale.domain, ticks: scale.ticks } : {})}
                />
                <Tooltip
                  formatter={(val, name) => [typeof val === 'number' ? fmtCurrency(val) : '—', name]}
                  contentStyle={{ fontSize: 12, borderRadius: 8 }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} iconSize={8} />
                {SERIES.map(s => (
                  <Bar
                    key={s.key}
                    dataKey={s.key}
                    name={s.label}
                    fill={s.fill}
                    fillOpacity={s.opacity}
                    radius={[3, 3, 0, 0]}
                    maxBarSize={34}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>

            {/* Exact figures. The chart is for shape; this is what reconciles to
                the per-year values the analyst typed — to the dollar. */}
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left font-medium text-muted-foreground py-1 pr-3">Metric</th>
                    {populatedRows.map(r => (
                      <th
                        key={r.year}
                        className={`text-right font-medium py-1 px-2 whitespace-nowrap ${
                          r.year === activeYear ? 'text-brand-primary' : 'text-muted-foreground'
                        }`}
                      >
                        {onSelectYear ? (
                          <button
                            type="button"
                            className="hover:underline"
                            onClick={() => onSelectYear(r.year)}
                            title={`Edit FY${r.year}`}
                          >
                            FY{r.year}
                          </button>
                        ) : (
                          `FY${r.year}`
                        )}
                      </th>
                    ))}
                    <th className="text-right font-medium text-muted-foreground py-1 pl-2 whitespace-nowrap">
                      YoY
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {SERIES.map(s => {
                    const latest = populatedRows[populatedRows.length - 1]
                    return (
                      <tr key={s.key} className="border-b border-border/50 last:border-0">
                        <td className="py-1 pr-3 text-brand-text">{s.label}</td>
                        {populatedRows.map(r => (
                          <td
                            key={r.year}
                            className={`text-right py-1 px-2 tabular-nums whitespace-nowrap ${
                              r.year === activeYear ? 'font-semibold text-brand-text' : ''
                            }`}
                          >
                            {cellText(r[s.key], fmtCurrency)}
                          </td>
                        ))}
                        <td className="text-right py-1 pl-2 tabular-nums whitespace-nowrap text-muted-foreground">
                          {fmtPct(growth[latest.year]?.[s.key] ?? null)}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {emptyYears.length > 0 && (
              <p className="text-[10px] text-muted-foreground mt-2">
                Not shown (no figures entered): {emptyYears.map(y => `FY${y}`).join(', ')}.
              </p>
            )}

            {/* ── TTM / blended summary ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              {ttm && <RollupTile
                heading={`TTM — FY${ttm.year}`}
                note="Latest populated year, carried through unchanged (annual data: TTM is that year)."
                revenue={ttm.revenue}
                adjEbitda={ttm.adjusted_ebitda}
                sde={ttm.sde}
                fmtCurrency={fmtCurrency}
              />}
              {average && <RollupTile
                heading={`${average.years_used}-Year Average`}
                note={`Simple mean of the ${average.years_used} populated ${average.years_used === 1 ? 'year' : 'years'} above.`}
                revenue={average.revenue ?? ''}
                adjEbitda={average.adjusted_ebitda ?? ''}
                sde={average.sde ?? ''}
                fmtCurrency={fmtCurrency}
              />}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

interface RollupTileProps {
  heading: string
  note: string
  revenue: Cell
  adjEbitda: Cell
  sde: Cell
  fmtCurrency: (val: number | null | undefined) => string
}

/** One summary block. `note` always states what the figure is, so a 2-of-3-year
 *  average can never be mistaken for a 3-year one. */
const RollupTile: React.FC<RollupTileProps> = ({ heading, note, revenue, adjEbitda, sde, fmtCurrency }) => (
  <div className="rounded-lg border border-border bg-muted/30 p-3">
    <p className="text-xs font-semibold text-brand-text">{heading}</p>
    <div className="grid grid-cols-3 gap-2 mt-2">
      <div>
        <p className="text-[10px] text-muted-foreground">Revenue</p>
        <p className="text-sm font-semibold tabular-nums">{cellText(revenue, fmtCurrency)}</p>
      </div>
      <div>
        <p className="text-[10px] text-muted-foreground">Adj. EBITDA</p>
        <p className="text-sm font-semibold tabular-nums text-brand-primary">
          {cellText(adjEbitda, fmtCurrency)}
        </p>
      </div>
      <div>
        <p className="text-[10px] text-muted-foreground">SDE</p>
        <p className="text-sm font-semibold tabular-nums">{cellText(sde, fmtCurrency)}</p>
      </div>
    </div>
    <p className="text-[10px] text-muted-foreground mt-2 leading-snug">{note}</p>
  </div>
)

export type { YearRollup }
